import { If } from 'components';
import { selectCurrentTeam } from 'containers/app/appSlice';
import {
  useAddCustomerAddressMutation,
  useEditCustomerAddressMutation,
  useLazyGetRecentSearchedCustomersQuery,
  useUpdateCustomerMutation
} from 'containers/customers/api';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import CreateCustomer from 'containers/create-customer/CreateCustomer';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import AddressModal from 'containers/jobs/components/AddressModal';
import {
  AddCustomerAddressPayload,
  CustomerAddress,
  CustomerObject,
  EditCustomerAddressPayload,
  UpdateCustomerPayload
} from 'containers/customers/customers.model';
import { CreateJobContextType } from 'containers/jobs/types';
import CustomerDetailsModal from 'containers/jobs/create-edit-job/components/CustomerDetailsModal';
import { AddressField, Customer } from 'containers/invoices/invoices.model';
import InvoiceBilledToSection from './InvoiceBilledToSection';
import { InvoiceTabEnum } from '../constants';
import { API_SOURCE } from 'constants/common';
import {
  checkIsCustomerAutoFilled,
  checkIsServiceAddressAutoFilled,
  validateInvoiceReminderPrerequisites
} from '../util';
import { getAddressAsString } from 'containers/jobs/utils';
import AutoFilledIndicator from 'components/auto-filled-indicator/AutoFilledIndicator';
import {
  disableCustomerNotSelectedError,
  selectInvoiceAutoFilledData,
  selectInvoiceDefaultsFromSetting,
  selectIsInvoiceFromEstimate,
  selectIsInvoiceFromJob,
  selectPaymentRemindersEnabled,
  selectShowCustomerNotSelectedError,
  updateAutoFilledData,
  updatePaymentRemindersEnabled
} from 'containers/invoices/InvoicesSlice';
import {
  disableCustomerNotSelectedErrorEditPage,
  selectEstimateIdForInvoiceEditPage,
  selectIsInvoiceFromJobEditPage,
  selectPaymentRemindersEnabledEditPage,
  selectShowCustomerNotSelectedErrorEditPage,
  updatePaymentRemindersEnabledEditPage
} from 'containers/invoices/edit-invoice/invoicesEditSlice';

interface InvoiceCustomerProps {
  isInvoiceEdit?: boolean;
  context: React.Context<CreateJobContextType>;
  selectedCustomer: CustomerObject | Customer;
  selectedServiceAddress: AddressField;
  selectedBillingAddress: AddressField;
  onUpdateCustomer: (custome) => void;
  onUpdateServiceAddress: (serviceAddress) => void;
  onUpdateBillingAddress: (billingAddress) => void;
  customerError?: string;
  onClearCustomerRiskError?: () => void;
}

const initlaAddressModalState = {
  type: null,
  isOpen: false,
  isEdit: false,
  address: null
};

const initialFormChangeState = {
  customer: false,
  serviceAddress: false,
  billingAddress: false
};

const getCustomerSectionBorderClass = (hasError: boolean, isTabSelected: boolean): string => {
  if (hasError) return 'border-error';
  if (isTabSelected) return 'border-secondary';
  return 'border-borderGray';
};

const InvoiceCustomer: FC<InvoiceCustomerProps> = ({
  isInvoiceEdit = false,
  context,
  selectedBillingAddress,
  selectedCustomer,
  selectedServiceAddress,
  onUpdateBillingAddress,
  onUpdateCustomer,
  onUpdateServiceAddress,
  customerError = '',
  onClearCustomerRiskError
}) => {
  const { selectedInvoiceTab, setSelectedInvoiceTab } = useContext<any>(context);
  const dispatch = useAppDispatch();
  const defaultRemindersFromSettings = useAppSelector(selectInvoiceDefaultsFromSetting);
  const showCustomerNotSelectedError = useAppSelector(
    isInvoiceEdit ? selectShowCustomerNotSelectedErrorEditPage : selectShowCustomerNotSelectedError
  );

  const remindersEnabled = useAppSelector(
    isInvoiceEdit ? selectPaymentRemindersEnabledEditPage : selectPaymentRemindersEnabled
  );
  const autoFilledData = useAppSelector(selectInvoiceAutoFilledData);
  const isFromJob = useAppSelector(isInvoiceEdit ? selectIsInvoiceFromJobEditPage : selectIsInvoiceFromJob);
  const isFromEstimate = useAppSelector(
    isInvoiceEdit ? state => Boolean(selectEstimateIdForInvoiceEditPage(state)) : selectIsInvoiceFromEstimate
  );
  const isCustomerSectionEditable = !isFromJob && !isFromEstimate;

  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState({
    customer: null,
    isOpen: false,
    type: CreateCustomerModalTypes.EDIT_CUSTOMER
  });
  const [openCustomerDetailsModal, setOpenCustomerDetailsModal] = useState(false);
  const [openAddressModal, setOpenAddressModal] = useState<{
    type: ADDRESS_TYPE;
    isOpen: boolean;
    isEdit: boolean;
    address;
  }>(initlaAddressModalState);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [hasChanged, setHasChanged] = useState(initialFormChangeState);

  const currentTeam = useAppSelector(selectCurrentTeam);

  const [
    updateCustomer,
    { isSuccess: updateCustomerSuccess, data: updatedCustomerData, isLoading: isUpdateCustomerInProgress }
  ] = useUpdateCustomerMutation();
  const [addCustomerAddressMutation, { isSuccess: isAddAddressSuccess, isLoading: isLoadingAddAddress }] =
    useAddCustomerAddressMutation();
  const [editCustomerAddressMutation, { isSuccess: isEditAddressSuccess, isLoading: isLoadingEditAddress }] =
    useEditCustomerAddressMutation();

  const [getRecentSearchedCustomersQuery, { data: getRecentSearchedCustomersResponse }] =
    useLazyGetRecentSearchedCustomersQuery();

  const { customers: recentCustomers = [] } = getRecentSearchedCustomersResponse ?? {};

  // Wrapper function to handle customer updates and disable error if needed
  const handleUpdateCustomer = (customer: CustomerObject | Customer | null) => {
    onUpdateCustomer(customer);
    onClearCustomerRiskError?.();
    // Disable error if customer is selected and error is showing
    if (customer && showCustomerNotSelectedError) {
      if (isInvoiceEdit) {
        dispatch(disableCustomerNotSelectedErrorEditPage());
      } else {
        dispatch(disableCustomerNotSelectedError());
      }
    }
  };

  const handleUpdateServiceAddress = (address: AddressField) => {
    onUpdateServiceAddress(address);
    onClearCustomerRiskError?.();
  };

  const handleUpdateBillingAddress = (address: AddressField) => {
    onUpdateBillingAddress(address);
    onClearCustomerRiskError?.();
  };

  const initialiseCustomerDetails = () => {
    const customer = { ...selectedCustomer };
    customer.serviceAddress = selectedServiceAddress ? [selectedServiceAddress] : [];
    customer.billingAddress = selectedBillingAddress ?? null;
    setCustomerDetails(customer);
  };

  const getRecentSearchedCustomers = () => {
    getRecentSearchedCustomersQuery({ teamId: currentTeam.id });
  };

  useEffect(() => {
    if (currentTeam?.id) {
      getRecentSearchedCustomers();
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    initialiseCustomerDetails();
  }, [selectedCustomer, selectedServiceAddress, selectedBillingAddress]);

  useEffect(() => {
    if (updateCustomerSuccess) {
      handleUpdateCustomer({
        ...updatedCustomerData,
        serviceAddress: selectedServiceAddress ? [selectedServiceAddress] : [],
        billingAddress: selectedBillingAddress ?? null
      });
      getRecentSearchedCustomers();
    }
  }, [updateCustomerSuccess]);

  useEffect(() => {
    if (isEditAddressSuccess || isAddAddressSuccess) {
      getRecentSearchedCustomers();
    }
  }, [isEditAddressSuccess, isAddAddressSuccess, isLoadingAddAddress, isLoadingEditAddress]);

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.BILL_TO;

  const { canEnable, customerSectionMsg = '' } = useMemo(() => {
    return validateInvoiceReminderPrerequisites(selectedCustomer as Customer);
  }, [selectedCustomer, defaultRemindersFromSettings?.remindersEnabled]);

  // Auto-disable reminders whenever customer changes if email is missing
  useEffect(() => {
    // Only validate if a customer exists (skip on mount when no customer is selected)
    if (!selectedCustomer) {
      return;
    }

    // If email is missing and reminders are enabled, disable them
    if (!canEnable && remindersEnabled) {
      if (isInvoiceEdit) {
        dispatch(updatePaymentRemindersEnabledEditPage(false));
      } else {
        dispatch(updatePaymentRemindersEnabled(false));
      }
    }
  }, [
    selectedCustomer,
    canEnable,
    remindersEnabled,
    isInvoiceEdit,
    dispatch,
    defaultRemindersFromSettings?.remindersEnabled
  ]);

  // Check if we should show the reminder info message (only when a customer exists & reminders are enabled in settings)
  const shouldShowReminderInfo = defaultRemindersFromSettings?.remindersEnabled && selectedCustomer && !canEnable;

  const handleOpenCustomerDetailsModal = () => {
    setHasChanged(initialFormChangeState);
    setOpenCustomerDetailsModal(true);
  };

  const onCustomerAdd = customerObject => {
    setCustomerDetails(prev => ({ ...prev, ...customerObject }));
    handleCloseCustomerModal();
    setHasChanged(prev => ({ ...prev, customer: true }));
  };

  const handleRemoveCustomerFromInvoice = () => {
    handleUpdateCustomer(null);
    // Reset isAnyCustomerData flag to behave like existing flow without autofill
    if (autoFilledData?.isAnyCustomerData) {
      dispatch(updateAutoFilledData({ isAnyCustomerData: false }));
    }
  };

  const handleCloseAddressModal = () => {
    setOpenAddressModal(initlaAddressModalState);
  };

  const handleAddressAddition = addressDetails => {
    const addressPayload: CustomerAddress = {
      ...addressDetails,
      teamId: currentTeam.id.toString(),
      customerId: selectedCustomer['ID'],
      isBillingSameAsService: false,
      addressId: 0,
      addressNote: addressDetails.note || '',
      addressName: addressDetails.saveAddressAs || ''
    };
    if (openAddressModal.type === ADDRESS_TYPE.SERVICE) {
      setCustomerDetails(prev => ({ ...prev, serviceAddress: [{ ...addressPayload }] }));
      setHasChanged(prev => ({ ...prev, serviceAddress: true }));
    } else if (openAddressModal.type === ADDRESS_TYPE.BILLING) {
      setCustomerDetails(prev => ({ ...prev, billingAddress: addressPayload }));
      setHasChanged(prev => ({ ...prev, billingAddress: true }));
    }
    handleCloseAddressModal();
  };

  const handleOpenAddressModal = ({ type, isEdit, address }: { type: ADDRESS_TYPE; isEdit: boolean; address }) => {
    setOpenAddressModal({ type, isOpen: true, isEdit, address });
  };

  const handleCloseCustomerDetailsModal = () => {
    setOpenCustomerDetailsModal(false);
    initialiseCustomerDetails();
  };

  const updateCustomerDetails = () => {
    const customer: UpdateCustomerPayload = {
      teamId: currentTeam.id.toString(),
      customerId: customerDetails.ID,
      name: customerDetails.name,
      lastName: customerDetails.lastName,
      companyName: customerDetails.companyName,
      phoneNumber: customerDetails.phoneNumber,
      email: customerDetails.email,
      referenceNumber: customerDetails.referenceNumber,
      note: customerDetails.note,
      source: API_SOURCE.WEB
    };
    updateCustomer(customer);
  };

  const updateAddressDetails = async (addressPayload: EditCustomerAddressPayload) => {
    const address = await editCustomerAddressMutation(addressPayload).unwrap();
    return address;
  };

  const addAddressDetails = async (addressPayload: AddCustomerAddressPayload) => {
    const address = await addCustomerAddressMutation(addressPayload).unwrap();
    return address;
  };

  const handleUpdateCustomerDetails = async (isServiceAddressEdit, isBillingAddressEdit, isBillingSameAsService) => {
    if (autoFilledData?.isAnyCustomerData && !autoFilledData.customer?.id) {
      // Customer from autofill with no ID - update state directly without API calls
      const updatedCustomer = {
        ...selectedCustomer,
        ...(hasChanged.customer ? customerDetails : {}),
        isBillingSameAsService
      };

      handleUpdateCustomer(updatedCustomer);

      if (hasChanged.serviceAddress && customerDetails.serviceAddress.length > 0) {
        handleUpdateServiceAddress(customerDetails.serviceAddress[0]);
      }
      if (hasChanged.billingAddress) {
        handleUpdateBillingAddress(customerDetails.billingAddress);
      }
    } else {
      // Customer with ID - follow existing pattern with API calls
      let serviceAddressId = customerDetails.serviceAddress[0]?.id ?? 0;

      if (hasChanged.customer) {
        updateCustomerDetails();
      }

      if (hasChanged.serviceAddress && customerDetails.serviceAddress.length > 0) {
        const serviceAddress: AddCustomerAddressPayload = {
          teamId: currentTeam.id.toString(),
          customerId: customerDetails.ID,
          type: ADDRESS_TYPE.SERVICE,
          street: customerDetails.serviceAddress[0].street,
          city: customerDetails.serviceAddress[0].city,
          state: customerDetails.serviceAddress[0].state,
          apartment: customerDetails.serviceAddress[0].apartment,
          zipCode: customerDetails.serviceAddress[0].zipCode,
          country: customerDetails.serviceAddress[0].country,
          addressNote: customerDetails.serviceAddress[0].addressNote?.trim(),
          addressName: customerDetails.serviceAddress[0].addressName,
          latitude: customerDetails.serviceAddress[0].latitude,
          longitude: customerDetails.serviceAddress[0].longitude,
          isBillingSameAsService: false,
          addressId: 0,
          source: API_SOURCE.WEB
        };
        if (isServiceAddressEdit) {
          const address = await updateAddressDetails({
            id: customerDetails.serviceAddress[0].id.toString(),
            body: serviceAddress
          });
          handleUpdateServiceAddress(address);
        } else {
          const address = await addAddressDetails(serviceAddress);
          serviceAddressId = address.id;
          handleUpdateServiceAddress(address);
        }
      }
      if (hasChanged.billingAddress || isBillingSameAsService) {
        const billingAddress: AddCustomerAddressPayload = {
          teamId: currentTeam.id.toString(),
          customerId: customerDetails.ID,
          type: ADDRESS_TYPE.BILLING,
          street: customerDetails.billingAddress ? customerDetails.billingAddress.street : '',
          city: customerDetails.billingAddress ? customerDetails.billingAddress.city : '',
          state: customerDetails.billingAddress ? customerDetails.billingAddress.state : '',
          apartment: customerDetails.billingAddress ? customerDetails.billingAddress.apartment : '',
          zipCode: customerDetails.billingAddress ? customerDetails.billingAddress.zipCode : '',
          country: customerDetails.billingAddress ? customerDetails.billingAddress.country : '',
          addressNote: customerDetails.billingAddress ? customerDetails.billingAddress.addressNote?.trim() : '',
          addressName: customerDetails.billingAddress ? customerDetails.billingAddress.addressName : '',
          latitude: customerDetails.billingAddress ? customerDetails.billingAddress.latitude : 0,
          longitude: customerDetails.billingAddress ? customerDetails.billingAddress.longitude : 0,
          isBillingSameAsService: isBillingSameAsService,
          addressId: serviceAddressId,
          source: API_SOURCE.WEB
        };

        if (isBillingSameAsService || !isBillingAddressEdit) {
          const address = await addAddressDetails(billingAddress);
          handleUpdateBillingAddress(address);
        } else {
          const address = await updateAddressDetails({
            id: customerDetails.billingAddress.id.toString(),
            body: billingAddress
          });
          handleUpdateBillingAddress(address);
        }
        handleUpdateCustomer({ ...customerDetails, isBillingSameAsService: isBillingSameAsService });
      }
    }
    handleCloseCustomerDetailsModal();
  };

  const handleBillingAddressSameAsService = (isSameAsServiceAddress: boolean) => {
    if (isSameAsServiceAddress) {
      setCustomerDetails(prev => ({ ...prev, billingAddress: prev.serviceAddress[0] }));
    } else {
      setCustomerDetails(prev => ({ ...prev, billingAddress: selectedBillingAddress ?? null }));
    }
  };

  const handleOpenCustomerModal = customer => {
    setOpenCreateCustomerModal({ isOpen: true, customer, type: CreateCustomerModalTypes.EDIT_CUSTOMER });
  };

  const handleCloseCustomerModal = () => {
    setOpenCreateCustomerModal({
      customer: null,
      isOpen: false,
      type: CreateCustomerModalTypes.EDIT_CUSTOMER
    });
  };

  const handleOnFocus = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.BILL_TO);
  };

  const isCustomerAutoFilled = useMemo(() => {
    const autoFilledCustomer = autoFilledData?.isAnyCustomerData ? autoFilledData?.customer : null;
    return checkIsCustomerAutoFilled(autoFilledCustomer, selectedCustomer, getAddressAsString);
  }, [autoFilledData?.customer, autoFilledData?.isAnyCustomerData, selectedCustomer]);

  const isServiceAddressAutoFilled = useMemo(() => {
    const autoFilledServiceAddress = autoFilledData?.customer?.serviceAddress?.[0] ?? null;
    return checkIsServiceAddressAutoFilled(autoFilledServiceAddress, selectedServiceAddress, getAddressAsString);
  }, [autoFilledData?.customer?.serviceAddress, selectedServiceAddress]);

  const isCustomerOrServiceAddressAutoFilled = isCustomerAutoFilled || isServiceAddressAutoFilled;

  return (
    <div className="w-full" onFocus={handleOnFocus}>
      <div className="mb-2 flex text-xl font-bold text-heading">
        Customer <span className="text-base text-error">*</span>{' '}
        <AutoFilledIndicator isAutoFilled={isCustomerOrServiceAddressAutoFilled} className="mt-1.5" />
      </div>

      <div
        className={`relative rounded-md border py-6 px-8 ${getCustomerSectionBorderClass(
          Boolean(customerError),
          isTabSelected
        )}`}>
        <InvoiceBilledToSection
          isEditable={isCustomerSectionEditable}
          selectedCustomer={selectedCustomer}
          selectedServiceAddress={selectedServiceAddress}
          handleRemoveCustomerFromInvoice={handleRemoveCustomerFromInvoice}
          handleOpenCustomerDetailsModal={handleOpenCustomerDetailsModal}
          recentCustomers={recentCustomers}
          customerError={customerError}
          onUpdateCustomer={handleUpdateCustomer}
          onUpdateServiceAddress={handleUpdateServiceAddress}
          onUpdateBillingAddress={handleUpdateBillingAddress}
          refetchRecentCustomers={getRecentSearchedCustomers}
          customerSectionInfo={shouldShowReminderInfo && !!customerSectionMsg ? customerSectionMsg : ''}
        />
        <If condition={showCustomerNotSelectedError}>
          <div className="my-2 text-error">Customer is missing</div>
        </If>
      </div>
      <If condition={Boolean(customerError)}>
        <div className="my-2 whitespace-pre-line text-sbase font-semibold text-error">{customerError}</div>
      </If>

      <If condition={Boolean(openCreateCustomerModal.isOpen)}>
        <CreateCustomer
          open={openCreateCustomerModal.isOpen}
          onClose={handleCloseCustomerModal}
          panelType={openCreateCustomerModal.type}
          customerData={openCreateCustomerModal.customer}
          customerDataFetchSuccess={true}
          customerDataFetchLoading={false}
          onAddCustomer={onCustomerAdd}
          defaultName={''}
        />
      </If>
      {openAddressModal.isOpen && (
        <AddressModal
          isEdit={openAddressModal.isEdit}
          type={openAddressModal.type}
          onClose={handleCloseAddressModal}
          initialAddress={openAddressModal.address}
          onAddAddress={handleAddressAddition}
          isLoading={isLoadingAddAddress || isLoadingEditAddress}
        />
      )}
      {openCustomerDetailsModal && (
        <CustomerDetailsModal
          onOpenAddressModal={handleOpenAddressModal}
          customer={customerDetails}
          onClose={handleCloseCustomerDetailsModal}
          onOpenCustomerModal={handleOpenCustomerModal}
          onUpdateCustomerDetails={handleUpdateCustomerDetails}
          hasValuesChanged={hasChanged.customer || hasChanged.serviceAddress || hasChanged.billingAddress}
          isUpdateInProgress={isLoadingAddAddress || isLoadingEditAddress || isUpdateCustomerInProgress}
          onToggleBillingAddressCheck={handleBillingAddressSameAsService}
        />
      )}
    </div>
  );
};

export default InvoiceCustomer;
